import React from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './ClientTestimonial.css';
import { useQuery } from '@tanstack/react-query';
import { Shimmer } from 'react-shimmer';
import api from '../services/api';

import testimonial1 from '../assets/images/story1.png';
import testimonial2 from '../assets/images/story2.png';
import { RightArrowSVG } from '../components/SvgIcons';

const useBlogs = () => {
  return useQuery({
    queryKey: ['blogs'],
    queryFn: async () => {
      const response = await api.get('/blogs/get-all-blogs', { params: { is_mobile: 0 } });
      return response.data;
    },
    staleTime: 3 * 60 * 1000,
    cacheTime: 3 * 60 * 1000,
    retry: 3,
  });
};

const StoryCard = ({ item }) => (
  <div className="story-content">
    <div className="story-img">
      <img src={item.img} alt={item.title} />
    </div>
    <div className="story-description">
      <span className="span-text">{item.category}</span>
      <h6 className="story-heading">
        <Link to={item.readMoreLink}>{item.title}</Link>
      </h6>
      <div className="story-text">
        <div className="story-left">
          {item.author} | {item.date}
        </div>
        <div className="story-right">
          <Link to={item.readMoreLink} className="read-btn">
            Read More <span><RightArrowSVG /></span>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const StoryTestimonial = () => {
  const { data: blogData, isLoading, isError, error } = useBlogs();

  const items =
    blogData?.dataset?.data?.blogs_list?.slice(0, 5)?.map((blog) => {
      const category = (
        Array.isArray(blog.categories)
          ? blog.categories[0]?.name
          : blog.categories?.split(', ')?.[0]
      ) || 'General';

      const childSlug = (blog.child_category_slug || 'general').toLowerCase();
      const parentSlug = (blog.parent_category_slug || '').toLowerCase();
      const safeSlug = blog.slug || btoa(String(blog.blog_id));

      return {
        img: blog.image_url || (blog.blog_id % 2 === 0 ? testimonial1 : testimonial2),
        category,
        title: blog.title,
        author: blog.author || 'Anonymous',
        date: blog.created_at || 'Unknown Date',
        readMoreLink: `/blog/${parentSlug ? `${encodeURIComponent(parentSlug)}/` : ''}${encodeURIComponent(childSlug)}/${encodeURIComponent(safeSlug)}`,
        slug: safeSlug,
      };
    }) || [];

  const count = items.length;

  if (!count) {
    return null;
  }

  const slidesToShow = Math.min(2, count);
  const infinite = count > slidesToShow;

  const settings = {
    dots: false,
    infinite,
    speed: 1000,
    slidesToShow,
    slidesToScroll: 1,
    autoplay: count > 1,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: count > 2,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: Math.min(2, count),
          slidesToScroll: 1,
          infinite: count > Math.min(2, count),
          arrows: count > 2,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: count > 1,
          arrows: count > 1,
        },
      },
    ],
  };

  const CustomPrevArrow = (props) => (
    <button {...props} className="slick-prev custom-arrow" aria-label="Previous">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
      </svg>
    </button>
  );

  const CustomNextArrow = (props) => (
    <button {...props} className="slick-next custom-arrow" aria-label="Next">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor" />
      </svg>
    </button>
  );

  if (isLoading) {
    return (
      <div className="testimonial-slider">
        <div className="shimmer-wrapper">
          {[0, 1].map((i) => (
            <div className="shimmer-item" key={i}>
              <div className="story-content">
                <div className="story-img">
                  <Shimmer width={300} height={200} />
                </div>
                <div className="story-description">
                  <Shimmer width={100} height={20} className="span-text" />
                  <Shimmer width={250} height={30} className="story-heading" />
                  <div className="story-text">
                    <div className="story-left"><Shimmer width={120} height={20} /></div>
                    <div className="story-right"><Shimmer width={100} height={20} /></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return <div className="testimonial-slider">Error: {error?.message || 'Failed to load blogs.'}</div>;
  }

  return (
    <div className="testimonial-slider">
      {count <= 1 ? (
        <div className="item" key={items[0]?.slug || 'single'}>
          <StoryCard item={items[0]} />
        </div>
      ) : (
        <Slider
          {...settings}
          prevArrow={<CustomPrevArrow />}
          nextArrow={<CustomNextArrow />}
        >
          {items.map((item) => (
            <div className="item" key={item.slug}>
              <StoryCard item={item} />
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default StoryTestimonial;
